import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const PROPERTY_TYPES = [
  { id: 'main_residence', name: 'Main Residence' },
  { id: 'rental_property', name: 'Rental Property' },
];

const PAYMENT_TYPES = [
  { id: 'repayment', name: 'Repayment' },
  { id: 'interest_only', name: 'Interest Only' },
];

const inputClass = 'border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-900/20 focus-visible:border-slate-400 rounded-lg h-10';
const labelClass = 'text-slate-900 font-medium';

interface EditItemModalProps {
  onClose: () => void;
  onSuccess: () => void;
  item: Record<string, unknown>;
  itemType: 'asset' | 'liability';
}

export default function EditItemModal({ onClose, onSuccess, item, itemType }: EditItemModalProps) {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: (item.name as string) || '',
    value: (item.value ?? item.amount ?? '').toString(),
    purchaseValue: (item.purchase_value ?? '').toString(),
    growthRate: (item.growth_rate ?? '3').toString(),
    description: (item.description as string) || '',
    interestRate: (item.interest_rate ?? '').toString(),
    propertyType: (item.subtype as string) || '',
    paymentType: (item.payment_type as string) || 'repayment',
    paymentAmount: (item.payment_amount ?? '').toString(),
    startDate: (item.start_date as string) || new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    const value = parseFloat(formData.value);
    if (isNaN(value) || value < 0) {
      setError('Please enter a valid value');
      return false;
    }
    if (item.type === 'real_estate') {
      const purchaseValue = parseFloat(formData.purchaseValue);
      if (isNaN(purchaseValue) || purchaseValue < 0) {
        setError('Please enter a valid purchase value');
        return false;
      }
    }
    if (item.type === 'mortgage') {
      const paymentAmount = parseFloat(formData.paymentAmount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        setError('Please enter a valid payment amount');
        return false;
      }
      if (!formData.startDate) {
        setError('Please enter a start date');
        return false;
      }
    }
    if (itemType === 'liability' && formData.interestRate) {
      const interestRate = parseFloat(formData.interestRate);
      if (isNaN(interestRate) || interestRate < 0) {
        setError('Please enter a valid interest rate');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!user) {
        setError('No user found. Please log in again.');
        return;
      }
      if (!validateForm()) return;

      setIsSubmitting(true);
      setError('');

      const value = parseFloat(formData.value);
      const baseData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
      };

      if (itemType === 'asset') {
        const { error: updateError } = await supabase
          .from('assets')
          .update({
            ...baseData,
            value,
            subtype: formData.propertyType || null,
            purchase_value: formData.purchaseValue ? parseFloat(formData.purchaseValue) : null,
            growth_rate: formData.growthRate ? parseFloat(formData.growthRate) : 3,
          })
          .eq('id', item.id);

        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await supabase
          .from('liabilities')
          .update({
            ...baseData,
            amount: value,
            interest_rate: formData.interestRate ? parseFloat(formData.interestRate) : null,
            payment_type: item.type === 'mortgage' ? formData.paymentType : null,
            payment_amount: item.type === 'mortgage' && formData.paymentAmount ? parseFloat(formData.paymentAmount) : null,
            start_date: item.type === 'mortgage' ? formData.startDate : null,
          })
          .eq('id', item.id);

        if (updateError) throw updateError;
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPropertyForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Property name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Property type</Label>
        <Select value={formData.propertyType || 'none'} onValueChange={(v) => setFormData({ ...formData, propertyType: v === 'none' ? '' : v })}>
          <SelectTrigger className={inputClass}>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Select type</SelectItem>
            {PROPERTY_TYPES.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={labelClass}>Purchase value</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.purchaseValue} onChange={(e) => setFormData({ ...formData, purchaseValue: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Current value</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} required />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 space-y-2">
          <Label className={labelClass}>Expected annual growth (%)</Label>
          <Input type="number" step="0.1" min="0" className={inputClass} value={formData.growthRate} onChange={(e) => setFormData({ ...formData, growthRate: e.target.value })} />
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="mt-8 shrink-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100">
                <Info className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs border-slate-200 bg-white text-slate-700">
              Expected annual appreciation. Default 3% is based on historical average.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
      </div>
    </div>
  );

  const renderMortgageForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Mortgage name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Outstanding balance</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Interest rate (%)</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.interestRate} onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Payment type</Label>
        <Select value={formData.paymentType} onValueChange={(v) => setFormData({ ...formData, paymentType: v })}>
          <SelectTrigger className={inputClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_TYPES.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={labelClass}>Monthly payment</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.paymentAmount} onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Start date</Label>
          <Input type="date" className={inputClass} value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
      </div>
    </div>
  );

  const renderStandardForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>{itemType === 'asset' ? 'Value' : 'Amount owed'}</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} required />
      </div>
      {itemType === 'liability' && (
        <div className="space-y-2">
          <Label className={labelClass}>Interest rate (%)</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.interestRate} onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })} />
        </div>
      )}
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
      </div>
    </div>
  );

  const renderForm = () => {
    if (item.type === 'real_estate') return renderPropertyForm();
    if (item.type === 'mortgage') return renderMortgageForm();
    return renderStandardForm();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-slate-200 rounded-2xl bg-white shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-slate-900 text-xl">
            Edit {itemType === 'asset' ? 'asset' : 'liability'}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Update the details below. Changes are saved when you click Save.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="destructive" className="mb-4 border-red-200 bg-red-50 [&>svg]:text-red-600">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {renderForm()}

          <DialogFooter className="mt-6 gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="rounded-full bg-slate-900 text-white hover:bg-slate-800 font-medium">
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
